import type { Resource } from 'i18next'
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import 'intl-pluralrules'

import languageDetector from './languageDetector'
import { normalizeI18nLanguage, SUPPORTED_I18N_LANGUAGES } from './locale'
import commonEn from './locales/en/common.json'
import dashboardEn from './locales/en/dashboard.json'
import libraryEn from './locales/en/library.json'
import notFoundEn from './locales/en/notFound.json'
import onboardingEn from './locales/en/onboarding.json'
import profileEn from './locales/en/profile.json'
import purchaseEn from './locales/en/purchase.json'
import sessionEn from './locales/en/session.json'
import setupEn from './locales/en/setup.json'
import statisticsEn from './locales/en/statistics.json'
import voucherEn from './locales/en/voucher.json'
import commonSv from './locales/sv/common.json'
import dashboardSv from './locales/sv/dashboard.json'
import librarySv from './locales/sv/library.json'
import notFoundSv from './locales/sv/notFound.json'
import onboardingSv from './locales/sv/onboarding.json'
import profileSv from './locales/sv/profile.json'
import purchaseSv from './locales/sv/purchase.json'
import sessionSv from './locales/sv/session.json'
import setupSv from './locales/sv/setup.json'
import statisticsSv from './locales/sv/statistics.json'
import voucherSv from './locales/sv/voucher.json'
import { DEFAULT_I18N_NAMESPACE, I18N_NAMESPACES } from './namespaces'

const resources: Resource = {
  en: {
    common: commonEn,
    dashboard: dashboardEn,
    library: libraryEn,
    onboarding: onboardingEn,
    notFound: notFoundEn,
    profile: profileEn,
    purchase: purchaseEn,
    session: sessionEn,
    setup: setupEn,
    statistics: statisticsEn,
    voucher: voucherEn,
  },
  sv: {
    common: commonSv,
    dashboard: dashboardSv,
    library: librarySv,
    onboarding: onboardingSv,
    notFound: notFoundSv,
    profile: profileSv,
    purchase: purchaseSv,
    session: sessionSv,
    setup: setupSv,
    statistics: statisticsSv,
    voucher: voucherSv,
  },
}

// eslint-disable-next-line import/no-named-as-default-member
const i18nInitPromise = i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_I18N_LANGUAGES],
    defaultNS: DEFAULT_I18N_NAMESPACE,
    ns: [...I18N_NAMESPACES],
    interpolation: {
      escapeValue: false, // react already safes from xss
    },
    react: {
      useSuspense: false,
    },
  })

// Public language changes must wait for i18next init because callers can run
// during app startup, before detector state and namespace setup are complete.
export async function changeLanguageWithLocalResources(
  language: string | null | undefined,
) {
  await i18nInitPromise

  const normalizedLanguage = normalizeI18nLanguage(language)

  // eslint-disable-next-line import/no-named-as-default-member
  await i18n.changeLanguage(normalizedLanguage)
}

// App startup waits for language detector state and namespace setup to complete.
export async function ensureI18nReady() {
  await i18nInitPromise
}

export default i18n
